import React, { useState, useEffect } from "react";
import TextEditor from "../components/TextEditor.jsx";
import "../static/css/AdminPublications.css";
import API_URL from "../config";

const AdminPublications = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [publicationContent, setPublicationContent] = useState("");

  const [publications, setPublications] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // Newsletter table state
  const [newsletterEntries, setNewsletterEntries] = useState([
    { id: 1, month: "", summary: "", attachment: "" }
  ]);


  useEffect(() => {
    fetchPublications();
  }, []);

  const fetchPublications = async () => {
    try {
      const response = await fetch(`${API_URL}/publications`);
      if (!response.ok) {
        const txt = await response.text();
        throw new Error(`Server returned ${response.status}: ${txt}`);
      }
      const data = await response.json();
      setPublications(data);
    } catch (error) {
      console.error("Error fetching publications:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this publication?")) {
      try {
        const response = await fetch(`${API_URL}/publications/delete/${id}`, {
          method: "DELETE",
        });
        if (response.ok) {
          alert("Publication deleted successfully");
          fetchPublications();
        } else {
          alert("Failed to delete publication");
        }
      } catch (error) {
        console.error("Error deleting publication:", error);
      }
    }
  };

  const handleEdit = async (pub) => {
    try {
      // Fetch full publication with newsletter entries
      const response = await fetch(`${API_URL}/publications/${pub._id}`);
      const fullPub = response.ok ? await response.json() : pub;

      setIsEditing(true);
      setEditId(fullPub._id);
      setTitle(fullPub.title || "");
      setDescription(fullPub.description || "");

      // populate newsletter entries
      if (fullPub.newsletterEntries && Array.isArray(fullPub.newsletterEntries)) {
        setNewsletterEntries(fullPub.newsletterEntries.map((e, idx) => ({ id: idx + 1, ...e })));
      } else {
        setNewsletterEntries([{ id: 1, month: "", summary: "", attachment: "" }]);
      }

      // Handle date formatting correctly
      if (fullPub.date) {
        const d = new Date(fullPub.date);
        if (!isNaN(d.getTime())) {
          setDate(d.toISOString().split("T")[0]);
        } else {
          setDate("");
        }
      } else {
        setDate("");
      }

      setPublicationContent(fullPub.content || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error fetching publication details:", error);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setDate("");
    setPublicationContent("");
    setIsEditing(false);
    setEditId(null);
    setNewsletterEntries([{ id: 1, month: "", summary: "", attachment: "" }]);
  };

  // Newsletter table handlers
  const addNewsletterRow = () => {
    setNewsletterEntries(prev => {
      const newId = prev.length > 0
        ? Math.max(...prev.map(e => e.id)) + 1
        : 1;
      return [
        ...prev,
        { id: newId, month: "", summary: "", attachment: "" }
      ];
    });
  };

  const removeNewsletterRow = (id) => {
    if (newsletterEntries.length > 1) {
      setNewsletterEntries(newsletterEntries.filter(entry => entry.id !== id));
    } else {
      alert("You must have at least one row");
    }
  };

  const updateNewsletterEntry = (id, field, value) => {
    setNewsletterEntries(prev => prev.map(entry =>
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
  };

  const handleAttachmentUpload = (id, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateNewsletterEntry(id, "attachment", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };





  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!title || !description) {
      alert("Please fill in the Title and Section Title.");
      return;
    }

    // Filter out rows that are completely empty to keep data clean
    const filteredEntries = newsletterEntries.filter(
      entry => entry.month || entry.summary || entry.attachment
    );

    // Strip local `id` field so Mongoose doesn't try to cast it as ObjectId
    const cleanedEntries = filteredEntries.map(({ id, ...rest }) => rest);

    const publicationData = {
      title,
      description,
      date,
      content: publicationContent,
      newsletterEntries: cleanedEntries,
    };

    const url = isEditing
      ? `${API_URL}/publications/update/${editId}`
      : `${API_URL}/publications/add`;
    const method = isEditing ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(publicationData),
      });

      if (response.ok) {
        alert(`Publication ${isEditing ? "updated" : "posted"} successfully!`);
        resetForm();
        fetchPublications(); // Refresh list
      } else {
        const errorMsg = await response.text();
        alert(`Failed to ${isEditing ? "update" : "post"} publication: ${errorMsg}`);
      }
    } catch (error) {
      console.error(`Error ${isEditing ? "updating" : "posting"} publication:`, error);
    }
  };

  return (
    <div className="admin-container">
      <div className="pub-content">
        <input
          type="text"
          placeholder="Title"
          className="article-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <textarea
          placeholder="Section Title"
          className="article-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />


        <div className="editor">
          <TextEditor
            content={publicationContent}
            setContent={setPublicationContent}
          />
        </div>


        {/* Newsletter Table Section */}
        <div style={{
          marginTop: "2rem",
          padding: "1.5rem",
          background: "#f9f9f9",
          borderRadius: "8px",
          border: "1px solid #ddd"
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem"
          }}>
            <h3 style={{ margin: 0, color: "#009688" }}>Table</h3>
            <button
              type="button"
              onClick={addNewsletterRow}
              style={{
                background: "#009688",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                fontSize: "24px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease"
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#00796b"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#009688"}
              title="Add new row"
            >
              +
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "white"
            }}>
              <thead>
                <tr style={{ background: "#009688", color: "white" }}>
                  <th style={{ padding: "12px", border: "1px solid #ddd", textAlign: "left", width: "160px" }}>
                    Date
                  </th>
                  <th style={{ padding: "12px", border: "1px solid #ddd", textAlign: "left" }}>
                    Summary
                  </th>
                  <th style={{ padding: "12px", border: "1px solid #ddd", textAlign: "left", width: "250px" }}>
                    Attachment
                  </th>
                  <th style={{ padding: "12px", border: "1px solid #ddd", width: "80px", textAlign: "center" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {newsletterEntries.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: "1px solid #ddd" }}>
                    <td style={{ padding: "8px", border: "1px solid #ddd", verticalAlign: "middle" }}>
                      <input
                        type="date"
                        value={entry.month || ""}
                        onChange={(e) => updateNewsletterEntry(entry.id, "month", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          fontSize: "14px",
                          boxSizing: "border-box"
                        }}
                      />
                    </td>
                    <td style={{ padding: "8px", border: "1px solid #ddd", verticalAlign: "middle" }}>
                      <textarea
                        placeholder="Key highlights include..."
                        value={entry.summary || ""}
                        onChange={(e) => updateNewsletterEntry(entry.id, "summary", e.target.value)}
                        rows="2"
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          fontSize: "14px",
                          resize: "vertical",
                          boxSizing: "border-box",
                          display: "block"
                        }}
                      />
                    </td>
                    <td style={{ padding: "8px", border: "1px solid #ddd", verticalAlign: "middle" }}>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => handleAttachmentUpload(entry.id, e.target.files[0])}
                        style={{
                          width: "100%",
                          padding: "4px",
                          fontSize: "12px",
                          boxSizing: "border-box"
                        }}
                      />
                      {entry.attachment && (
                        <div style={{ marginTop: "4px" }}>
                          <span style={{
                            fontSize: "12px",
                            color: "#009688",
                            display: "block"
                          }}>
                            ✓ File uploaded
                          </span>
                          {typeof entry.attachment === 'string' && entry.attachment.startsWith('data:') && (
                            <a 
                              href={entry.attachment} 
                              target="_blank" 
                              rel="noreferrer"
                              style={{ fontSize: '10px', color: '#2196F3', textDecoration: 'none' }}
                            >
                              View uploaded file
                            </a>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "8px", border: "1px solid #ddd", textAlign: "center", verticalAlign: "middle" }}>
                      <button
                        type="button"
                        onClick={() => removeNewsletterRow(entry.id)}
                        style={{
                          background: "#f44336",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          padding: "8px 12px",
                          cursor: "pointer",
                          fontSize: "18px",
                          transition: "background 0.3s ease",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#d32f2f"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "#f44336"}
                        title="Remove row"
                      >
                        −
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{
            marginTop: "1rem",
            padding: "10px",
            background: "#e8f5e9",
            borderRadius: "4px",
            fontSize: "14px",
            color: "#2e7d32"
          }}>
            <strong>Total Entries:</strong> {newsletterEntries.length}
          </div>
        </div>


        <button className="post-button" onClick={handleSubmit}>
          {isEditing ? "UPDATE Blog" : "POST Blog"}
        </button>
        {isEditing && (
          <button
            className="post-button"
            onClick={resetForm}
            style={{ marginTop: '10px', background: '#888' }}
          >
            Cancel Edit
          </button>
        )}

        <div className="existing-publications" style={{ marginTop: '40px', borderTop: '1px solid #ddd', paddingTop: '20px' }}>
          <h3>Existing Publications</h3>
          <div className="publication-list" style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {publications.map((pub) => (
              <div key={pub._id} className="publication-item">
                <h4 style={{ margin: '0 0 5px 0', color: '#009688' }}>{pub.title || 'No Title'}</h4>
                <p style={{ fontWeight: 'bold', margin: '0 0 10px 0' }}>{pub.description?.substring(0, 50)}</p>
                <p style={{ fontSize: '0.85em', color: '#888', margin: '0 0 10px 0' }}>
                  {pub.date ? new Date(pub.date).toLocaleDateString() : 'No Date'}
                </p>
                <p style={{ fontSize: '0.9em', color: '#555', marginBottom: '5px' }}>{pub.content?.substring(0, 100).replace(/<[^>]*>/g, '') || 'No content'}...</p>
                {pub.newsletterEntries && pub.newsletterEntries.length > 0 && (
                  <p style={{ fontSize: '0.8em', color: '#2e7d32', fontWeight: 'bold' }}>
                    Table Entries: {pub.newsletterEntries.length}
                  </p>
                )}
                <div className="item-actions">
                  <button
                    className="edit-btn"
                    onClick={() => handleEdit(pub)}
                  >
                    Edit
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(pub._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPublications;
